// Exit-intent dialog (CRO playbook 7.1), page-aware.
//
// sections/mdc-exit-intent.liquid renders up to three panels - subscribe, product, cart -
// and says which one this page defaults to (data-page). At the moment the exit gesture
// happens, this re-checks the cart: if the cart drawer is open, or we are on /cart, and
// the cart is not yet at the free-shipping threshold, the cart offer wins.
//
// Triggers
//   Desktop: the pointer leaves through the top of the window (towards tabs / close).
//   Phones:  a fast flick back to the top of the page. Deliberately NOT the history
//            trap most scripts use for the Back button - it holds people who are trying
//            to leave, and Chrome skips history entries added without a user gesture.
//
// A native <dialog> opened with showModal() gives the focus trap and the Esc key for
// free, so none of that is reimplemented here.
//
// Preview: ?mdc-exit on any URL opens it after half a second, ignoring the frequency cap
// and the arming delay, and does not record that it was seen.
(function () {
  var root = document.querySelector('[data-mdc-exit]');
  if (!root) return;

  var dialog = root.querySelector('dialog');
  if (!dialog || typeof dialog.showModal !== 'function') return;

  var preview = /[?&]mdc-exit(=|&|$)/.test(window.location.search);
  var days = parseInt(root.dataset.frequency, 10);
  if (!Number.isFinite(days) || days < 0) days = 7;
  var armAfter = (parseInt(root.dataset.delay, 10) || 0) * 1000;
  var threshold = parseInt(root.dataset.freeShipping, 10) || 0; // in bani, like cart.js
  var pageVariant = root.dataset.page || 'none';
  var KEY = 'mdc-exit-seen';
  var PLACEHOLDER = '[Placeholder';

  function seenRecently(variant) {
    try {
      var at = parseInt(localStorage.getItem(KEY) || '0', 10);
      if (!at) return false;
      return Date.now() - at < days * 86400000;
    } catch (e) {
      // Private mode or blocked storage: better to stay quiet than to nag every page.
      return true;
    }
  }

  function remember(variant) {
    if (preview) return;
    try {
      localStorage.setItem(KEY, String(Date.now()));
    } catch (e) {}
  }

  // One shared cap: after any panel is shown, none shows again for `days` days.

  function has(variant) {
    return !!root.querySelector('[data-variant="' + variant + '"]');
  }

  function realCode(code) {
    return !!code && code.indexOf(PLACEHOLDER) !== 0;
  }

  function drawerOpen() {
    var drawer = document.getElementById('cart-drawer');
    if (!drawer) return false;
    return drawer.hasAttribute('open') || !!drawer.querySelector('dialog[open]');
  }

  // Resolves to the panel to show, or null for "show nothing".
  function pickVariant() {
    var cartContext = pageVariant === 'cart' || drawerOpen();
    var fallback = pageVariant === 'cart' || pageVariant === 'none' ? null : pageVariant;
    if (!cartContext || !has('cart')) return Promise.resolve(fallback);

    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var qualifies = cart.item_count > 0 && (!threshold || cart.total_price < threshold);
        return qualifies ? 'cart' : fallback;
      })
      .catch(function () { return fallback; });
  }

  function show(variant) {
    if (!variant || !has(variant)) return false;
    root.querySelectorAll('[data-variant]').forEach(function (panel) {
      panel.hidden = panel.dataset.variant !== variant;
    });
    dialog.setAttribute('aria-labelledby', 'mdc-exit-title-' + variant);
    dialog.showModal();
    return true;
  }

  var armed = false;
  var busy = false;
  var fired = false;

  function open() {
    if (fired || busy || !armed || dialog.open) return;
    // Never interrupt someone who is typing.
    var active = document.activeElement;
    if (active && active.matches && active.matches('input, textarea, select')) return;
    busy = true;
    pickVariant().then(function (variant) {
      busy = false;
      if (fired || (!preview && variant && seenRecently(variant))) return;
      if (show(variant)) {
        fired = true;
        remember(variant);
      }
    });
  }

  setTimeout(function () {
    armed = true;
    if (preview) open();
  }, preview ? 500 : armAfter);

  // Desktop: pointer leaves through the top of the viewport.
  document.addEventListener('mouseout', function (event) {
    if (event.relatedTarget || event.clientY > 0) return;
    open();
  });

  // Phones: a fast flick upward, back towards the top of the page.
  var lastY = window.scrollY;
  var lastT = Date.now();
  window.addEventListener(
    'scroll',
    function () {
      var y = window.scrollY;
      var t = Date.now();
      var dt = t - lastT || 1;
      var speed = (lastY - y) / dt;
      lastY = y;
      lastT = t;
      if (window.innerWidth >= 750) return;
      if (speed > 1.6 && y < 220) open();
    },
    { passive: true }
  );

  root.querySelectorAll('[data-mdc-exit-close]').forEach(function (btn) {
    btn.addEventListener('click', function () { dialog.close(); });
  });

  // Clicking the dim backdrop closes it too - the dialog element itself is the target
  // only when the click lands outside its box.
  dialog.addEventListener('click', function (event) {
    if (event.target === dialog) dialog.close();
  });

  root.querySelectorAll('[data-mdc-exit-copy]').forEach(function (chip) {
    if (!navigator.clipboard) return;
    chip.addEventListener('click', function () {
      navigator.clipboard.writeText(chip.dataset.mdcExitCopy || '').then(function () {
        var old = chip.textContent;
        chip.textContent = chip.dataset.copied || 'Copiat';
        setTimeout(function () { chip.textContent = old; }, 1800);
      }, function () {});
    });
  });

  // ---- Subscribe panel -------------------------------------------------------------
  // Shopify's own customer form, posted in the background so the shopper stays on the
  // page and sees the code at once. Shopify redirects a successful post to a URL with
  // customer_posted=true. If it asks for a captcha instead (/challenge), the form is
  // submitted normally so the shopper can complete it - nothing is lost.
  var subscribe = document.getElementById('mdc-exit-subscribe');
  if (subscribe) {
    var panel = subscribe.closest('[data-variant]');
    var error = panel.querySelector('[data-mdc-exit-error]');
    subscribe.addEventListener('submit', function (event) {
      event.preventDefault();
      var button = subscribe.querySelector('button[type="submit"]');
      button.setAttribute('aria-busy', 'true');
      error.hidden = true;

      fetch(subscribe.action, { method: 'POST', body: new FormData(subscribe), credentials: 'same-origin' })
        .then(function (response) {
          if (/\/challenge/.test(response.url)) {
            subscribe.submit();
            return;
          }
          if (!/customer_posted=true/.test(response.url)) throw new Error('not posted');
          panel.querySelector('[data-mdc-exit-step="form"]').hidden = true;
          panel.querySelector('[data-mdc-exit-step="done"]').hidden = false;
          var chip = panel.querySelector('[data-mdc-exit-copy]');
          if (chip) chip.focus();
        })
        .catch(function () {
          error.hidden = false;
        })
        .finally(function () {
          button.removeAttribute('aria-busy');
        });
    });
  }

  // ---- Product panel ---------------------------------------------------------------
  // Adds this product to the cart, applies the code to the cart itself (/cart/update.js
  // with "discount", the same thing Shopify's /discount/ link does, and it carries through
  // to checkout), then opens the cart drawer so the shopper sees the reduced line right
  // away and stays on the page. On /cart the page is reloaded instead, since it IS the
  // cart. Any failure falls back to the plain link, which applies the code and returns
  // to the product.
  var SECTION_ID = 'cart-drawer-section';
  var HOST_ID = 'shopify-section-' + SECTION_ID;

  function refreshBubbles(cart) {
    var count = (cart && cart.item_count) || 0;
    document.querySelectorAll('.cart-bubble').forEach(function (bubble) {
      bubble.classList.toggle('visually-hidden', count === 0);
      var value = bubble.querySelector('.cart-bubble__text-count');
      if (!value) return;
      value.classList.toggle('hidden', count === 0);
      value.textContent = count > 99 ? '99+' : String(count);
    });
  }

  function openDrawer() {
    var go = function () {
      var drawer = document.getElementById('cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    };
    if (window.customElements && !customElements.get('theme-drawer')) {
      customElements.whenDefined('theme-drawer').then(function () { requestAnimationFrame(go); });
    } else {
      requestAnimationFrame(go);
    }
  }

  var add = root.querySelector('[data-mdc-exit-add]');
  if (add) {
    add.addEventListener('click', function (event) {
      event.preventDefault();
      add.setAttribute('aria-busy', 'true');
      var code = add.dataset.code;
      var onCartPage = pageVariant === 'cart' || /^\/cart\/?$/.test(window.location.pathname);
      var hasDrawer = !!document.getElementById(HOST_ID);
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(add.dataset.mdcExitAdd), quantity: 2 }] })
      })
        .then(function (response) {
          if (!response.ok) throw new Error('add failed');
          var body = { sections: SECTION_ID, sections_url: window.location.pathname };
          if (realCode(code)) body.discount = code;
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body)
          });
        })
        .then(function (response) {
          if (!response.ok) throw new Error('discount failed');
          return response.json();
        })
        .then(function (cart) {
          if (onCartPage || !hasDrawer) {
            window.location.href = '/cart';
            return;
          }
          var markup = cart.sections && cart.sections[SECTION_ID];
          var host = document.getElementById(HOST_ID);
          if (markup && host) {
            var fresh = new DOMParser().parseFromString(markup, 'text/html').getElementById(HOST_ID);
            if (fresh) host.replaceWith(fresh);
          }
          refreshBubbles(cart);
          add.removeAttribute('aria-busy');
          dialog.close();
          openDrawer();
        })
        .catch(function () {
          window.location.href = realCode(code)
            ? '/discount/' + encodeURIComponent(code) + '?redirect=/cart'
            : add.href;
        });
    });
  }

  // ---- Cart panel ------------------------------------------------------------------
  // Straight to checkout with the free-shipping code applied.
  var checkout = root.querySelector('[data-mdc-exit-checkout]');
  if (checkout) {
    var code = checkout.dataset.code;
    checkout.href = realCode(code)
      ? '/discount/' + encodeURIComponent(code) + '?redirect=/checkout'
      : '/checkout';
  }
})();
